import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { Typography, Box, Divider, Button, Skeleton, Alert } from '@mui/material';
import { Room as RoomIcon, Schedule as ScheduleIcon, Language as LanguageIcon } from '@mui/icons-material';
import { formatAddress, formatTime } from '../../../helpers/formatters';
import PagePaper from '../../../components/Common/PagePaper';
import EventDate from './EventDate';
import { Colors } from '../../../assets/theme/Colors';
import EventShift from './EventShift';
import useActiveSite from '../../../hooks/useActiveSite';
import { fetchSiteEventIfNeeded } from '../../../actions/sites';

const handleAddressClick = (address) => window.open(`https://maps.google.com/?q=${address}`, '_blank');

const EventDetailsSkeleton = () => (
  <>
    <PagePaper style={{ marginBottom: 8 }}>
      <Box display="grid" gridTemplateColumns="0.2fr 1fr">
        <Box gridColumn={1} p={2} sx={{ textAlign: 'center', width: '100%' }}>
          <Typography>
            <Skeleton />
          </Typography>
          <Typography sx={{ fontSize: '1.5rem' }}>
            <Skeleton />
          </Typography>
        </Box>
        <Box gridColumn={2} p={2}>
          <Typography variant="h5" paragraph>
            <Skeleton />
          </Typography>
        </Box>
      </Box>

      <Divider />

      <Box display="grid" rowGap={2} p={2}>
        <Box display="flex" alignItems="center">
          <Skeleton variant="circular" width={24} height={24} sx={{ marginRight: '10px', flexShrink: 0 }} />
          <Box display="flex" flexDirection="column">
            <Typography variant="body1">
              <Skeleton width={270} />
            </Typography>
            <Typography variant="body1">
              <Skeleton width={70} />
            </Typography>
          </Box>
        </Box>
        <Box display="flex" alignItems="center">
          <Skeleton variant="circular" width={24} height={24} sx={{ marginRight: '10px', flexShrink: 0 }} />
          <Typography variant="body1">
            <Skeleton width={250} />
          </Typography>
        </Box>
      </Box>
    </PagePaper>

    <PagePaper style={{ marginBottom: 8 }}>
      <Box p={2}>
        <Typography variant="subtitle1">
          <Skeleton width={110} />
        </Typography>
        <Typography variant="body2">
          <Skeleton />
        </Typography>
      </Box>
    </PagePaper>

    <PagePaper>
      <Box p={2}>
        <Typography variant="subtitle1" gutterBottom>
          <Skeleton width={60} />
        </Typography>

        <Box display="grid" rowGap={2}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" height={72} />
          ))}
        </Box>
      </Box>
    </PagePaper>
  </>
);

const EventDetails = ({
  eventData,
  onShiftButtonClick,
  onAdoptClick,
  isLoading,
  submittingShift,
  onShiftManagerClick = () => {},
  showShiftManagerMobile = false,
}) => {
  const activeSite = useActiveSite();
  const dispatch = useDispatch();
  const eventSpecsEntities = useSelector((state) => state.entities.sites.events).bySiteId[[activeSite.id]];
  const eventSpecs = eventSpecsEntities ? eventSpecsEntities.filter((e) => e.id === eventData?.event_spec_id)[0] : null;
  const eventDate = isLoading || !eventData ? null : moment(eventData.date?.value, eventData.date?.format);
  const eventAddress = isLoading ? <Skeleton width={250} /> : formatAddress(eventData?.location);
  const eventTime = isLoading ? null : `${formatTime(eventData.begin?.value)} - ${formatTime(eventData.end?.value)}`;
  const isEventFromPast = moment(eventDate).isBefore(moment().startOf('day'));

  useEffect(() => {
    if (eventData?.event_spec_id) {
      // refetch quietly when already loaded - a non-quiet request drops the specs from the store
      dispatch(fetchSiteEventIfNeeded(activeSite.id, eventData.event_spec_id, !!eventSpecs));
    }
  }, [eventData]);

  // the event specs decide whether the "Adopt" buttons are rendered, so wait for them to avoid a layout shift
  const isEventSpecsLoading = !!eventData?.event_spec_id && !eventSpecs;

  if (isLoading || isEventSpecsLoading) {
    return <EventDetailsSkeleton />;
  }

  return (
    <>
      <PagePaper style={{ marginBottom: 8 }}>
        {!isLoading && isEventFromPast && (
          <Alert severity="info">This event has ended! Therefore you are not able to join or leave it!</Alert>
        )}
        <Box display="grid" gridTemplateColumns="0.2fr 1fr">
          <EventDate date={eventDate} gridColumn={1} p={2} />
          <Box gridColumn={2} p={2}>
            <Typography variant="h5" paragraph data-testid="event-details-name">
              {eventData.name}
            </Typography>
            {showShiftManagerMobile && (
              <Button variant="contained" color="primary" onClick={() => onShiftManagerClick(eventData?.event_spec_id)}>
                Shift Manager
              </Button>
            )}
          </Box>
        </Box>

        <Divider />

        <Box display="grid" rowGap={2} p={2}>
          <Box display="flex" alignItems="center">
            <ScheduleIcon style={{ color: Colors.appBar.main, marginRight: 10 }} />
            <Box display="flex" flexDirection="column">
              <Typography variant="body1" data-testid="event-details-date">
                {`${eventDate.format('dddd, MMMM DD, YYYY')}`}
              </Typography>
              <Typography variant="body1" data-testid="event-details-time">
                {eventTime}
              </Typography>
            </Box>
          </Box>
          {eventData && eventData.digital && (
            <Box display="flex" alignItems="center">
              <LanguageIcon style={{ color: Colors.appBar.main, marginRight: 10 }} />
              <Typography variant="body1" data-testid="event-digital">
                Virtual Event
              </Typography>
            </Box>
          )}
          {eventData && !eventData.digital && eventAddress && (
            <Box
              onClick={() => handleAddressClick(eventAddress)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              <RoomIcon style={{ color: Colors.appBar.main, marginRight: 10 }} />
              <Typography variant="body1" data-testid="event-details-address">{eventAddress}</Typography>
            </Box>
          )}
        </Box>
      </PagePaper>

      {eventData.description && (
        <PagePaper style={{ marginBottom: 8 }} data-testid="event-details-description">
          <Box p={2}>
            <Typography variant="subtitle1">Event Details</Typography>
            <Typography variant="body2" data-testid="event-details-description-text">
              {eventData.description}
            </Typography>
          </Box>
        </PagePaper>
      )}

      <PagePaper data-testid="event-details-shifts">
        <Box p={2}>
          <Typography variant="subtitle1" gutterBottom>
            Shifts
          </Typography>

          <Box display="grid" rowGap={2}>
            {!isLoading &&
              eventData.shifts
                .filter((shift) => shift.deleted_at === null)
                .map((shift, index) => (
                  <EventShift
                    key={shift.id}
                    index={index}
                    isSubmitting={submittingShift === shift.id}
                    disabled={!!submittingShift || isEventFromPast}
                    isLoading={isLoading}
                    handleShiftButtonClick={onShiftButtonClick}
                    handeShiftAdoptClick={onAdoptClick}
                    shift={shift}
                    eventData={eventData}
                    eventSpecs={eventSpecs}
                  />
                ))}
          </Box>
        </Box>
      </PagePaper>
    </>
  );
};

export default EventDetails;
